"use client";

import { NameWithCode } from "@/components/name-with-code";
import { deleteSalaryScale } from "@/features/policies/salary-scales";
import { DataTable, DataTableColumnHeader } from "@/components/data-table";
import DeleteDialog from "@/components/delete-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EPermission } from "@/constants/permission.enum";
import { ISalaryScale } from "@/features/policies/salary-scales";
import { useDataTable } from "@/hooks/use-data-table";
import { serverActionMessage, withRBAC } from "@/lib/action-utils";
import { PermissionDeniedError } from "@/lib/errors";
import { formatCurrency, formatDate, getBaseResourceUrl } from "@/lib/utils";
import { PagedList } from "@/types/paged-list-type";
import { ColumnDef } from "@tanstack/react-table";
import { EllipsisVertical } from "lucide-react";
import { useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { Fragment, useState } from "react";
import { toast } from "sonner";

interface SalaryScaleDataTableProps {
  pagedList: PagedList<ISalaryScale>;
}

export function SalaryScaleDataTable({ pagedList }: SalaryScaleDataTableProps) {
  const t = useTranslations();
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ISalaryScale | null>(null);

  const router = useRouter();
  const pathname = usePathname();

  const columns: ColumnDef<ISalaryScale>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("feature.salary-scale.name")}
        />
      ),
      cell: ({ row }) => (
        <NameWithCode
          name={row.original.name}
          code={row.original.code}
          capitalizeName
        />
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "baseSalary",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("feature.salary-scale.base-salary")}
          className="text-right"
        />
      ),
      cell: ({ row }) => (
        <span className="capitalize">
          {formatCurrency(row.original.baseSalary)}
        </span>
      ),
      meta: { align: "right" },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "effectiveDate",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("feature.salary-scale.effective-date")}
        />
      ),
      cell: ({ row }) => formatDate(row.original.effectiveDate),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "expirationDate",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("feature.salary-scale.expiration-date")}
        />
      ),
      cell: ({ row }) => formatDate(row.original.expirationDate),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "createdDate",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("feature.common.created-date")}
        />
      ),
      cell: ({ row }) => (
        <div className="leading-tight">
          <div>{formatDate(row.original.createdDate)}</div>
          <div className="text-xs text-muted-foreground">{row.original.createdByUser}</div>
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "lastModifiedDate",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("feature.common.last-modified-date")}
        />
      ),
      cell: ({ row }) => row.original.lastModifiedDate ? (
        <div className="leading-tight">
          <div>{formatDate(row.original.lastModifiedDate)}</div>
          <div className="text-xs text-muted-foreground">{row.original.modifiedByUser}</div>
        </div>
      ) : null,
      enableSorting: true,
    },
    {
      id: "actions",
      cell: function Cell({ row }) {
        const handleEdit = () => {
          router.push(`${pathname}/add-salary-scale/${row.original.id}`, {
            scroll: false,
          });
        };

        const onManageGrades = () => {
          const params = new URLSearchParams({
            scale_id: row.original.id.toString(),
          });
          router.push(`/policies/salary-grades?${params}`, {
            scroll: false,
          });
        };

        const handleDelete = () => {
          setIsOpenDialog(true);
          setSelectedRow(row.original);
        };

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <span className="flex cursor-pointer items-center justify-center data-[state=open]:bg-muted">
                <EllipsisVertical className="size-4" aria-hidden="true" />
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onSelect={handleEdit}>
                {t("action.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onManageGrades}>
                {t("action.manage.salary-grade")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleDelete}>
                {t("action.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const handleDelete = async () => {
    try {
      if (selectedRow) {
        const protectedDelete = withRBAC({
          requiredPermissions: [EPermission.Delete],
          resourcePath: getBaseResourceUrl(pathname),
        })(async () => {
          return await deleteSalaryScale(selectedRow.id);
        });
        const response = await protectedDelete();

        serverActionMessage({
          response,
          successMessage: t("notification.deleted-successfully"),
          errorMessage: t("notification.update-failed"),
        });

        setIsOpenDialog(false);
        setSelectedRow(null);
      }
    } catch (error) {
      if (error instanceof PermissionDeniedError) {
        toast.error(error.message);
      } else {
        toast.error(t("notification.update-failed"));
      }
    }
  };

  const { table } = useDataTable({
    data: pagedList.results,
    columns,
    pageCount: pagedList.totalPages,
    defaultPerPage: pagedList.pageSize,
  });

  return (
    <Fragment>
      <DataTable table={table} />
      <DeleteDialog
        isOpen={isOpenDialog}
        setIsOpen={setIsOpenDialog}
        handleDelete={handleDelete}
      />
    </Fragment>
  );
}
